import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { GridCanvas } from "./GridCanvas";
import { EASE } from "@/lib/motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwdHJ1Y2slMjBoaWdod2F5JTIwZGFyayUyMG5pZ2h0fGVufDB8fHx8MTc4NDY1NzMzN3ww&ixlib=rb-4.1.0&q=85";

const lines = ["FREIGHT MOVED", "WITH ZERO", "FRICTION."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: EASE },
  }),
};

export const Hero = ({ onQuote, onCarrier }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden" data-testid="hero">
      {/* Background image + parallax */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="Freight truck on highway at night" className="h-[130%] w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      {/* Node network */}
      <div className="absolute inset-0 z-[1] opacity-70">
        <GridCanvas />
      </div>

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1500px] flex-col justify-center px-6 pt-28 lg:px-12"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-neon"
        >
          <span className="h-px w-10 bg-neon" />
          USA Freight Brokerage — Est. 2025
        </motion.div>

        <h1 className="font-display text-5xl font-bold uppercase leading-[0.92] tracking-tighter text-white md:text-7xl lg:text-[7.5vw]">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg"
        >
          ApexLink connects shippers with vetted carriers across North America — full truckload,
          LTL, intermodal and expedited freight, orchestrated by real people and real-time data.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button
            onClick={onQuote}
            data-testid="hero-quote-btn"
            className="group flex items-center justify-center gap-2 bg-neon px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-white"
          >
            Request a Quote
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={onCarrier}
            data-testid="hero-carrier-btn"
            className="group flex items-center justify-center gap-2 border border-white/25 px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-neon hover:text-neon"
          >
            Become a Carrier
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400"
      >
        Scroll <ArrowDown size={14} className="animate-bounce text-neon" />
      </motion.div>
    </section>
  );
};
